import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    ItemSeparatorInsetConstantType,
    ItemTitleVariantConstantType,
    ItemGroupRadiusConstantType,
    ItemAvatarSizeConstantType,
    ItemMediaWidthConstantType,
    ItemMeasuresConstantType,
    ItemIconSizeConstantType,
    ItemRadiusConstantType,
    ItemColorsConstantType,
    ItemDynamicStyleType,
    ItemAvatarSize,
    ItemMeasures,
    ItemColors,
    ItemSize
} from "./type";
import type {
    Mutable
} from "../../types";
import {
    ROW_CARD_SELECTED_TITLE_VARIANTS,
    getRowCardContainerColor,
    getRowCardColors
} from "../rowCard/stylesheet";
import {
    getCornersStyle,
    getCornerStyle
} from "../../helpers/theme/corner";
import {
    getFocusRingStyle
} from "../../helpers/theme/state";
import {
    concentric
} from "../../helpers/theme/concentric";
import {
    getRowIconGap
} from "../../helpers/theme/control";
import {
    getMotionTransition
} from "../../helpers/motion";
import {
    webStyle
} from "../../utils";

export const ITEM_TITLE_VARIANTS: Record<ItemSize, keyof NCoreUIKit.Typography> = {
    medium: "labelMediumSize",
    small: "labelMediumSize",
    xSmall: "labelSmallSize"
};

export const ITEM_MEDIA_TOP_ALIGN_LINES = 1.5;

export const ITEM_DESCRIPTION_VARIANTS: Record<ItemSize, keyof NCoreUIKit.Typography> = {
    medium: "bodyMediumSize",
    small: "bodyMediumSize",
    xSmall: "bodySmallSize"
};

export const ITEM_MIN_HEIGHT_KEYS: Record<ItemSize, keyof NCoreUIKit.ControlHeights> = {
    medium: "large",
    small: "medium",
    xSmall: "small"
};

export const ITEM_SMALL_ICON_SIZES: Array<ItemSize> = [
    "xSmall"
];

export const getItemMeasures = ({
    controlHeights,
    touchTargets,
    pointerType,
    spaces,
    size
}: ItemMeasuresConstantType): ItemMeasures => {
    const baseHeight = controlHeights[ITEM_MIN_HEIGHT_KEYS[size]];

    const minHeight = pointerType === "coarse" ? Math.max(baseHeight, touchTargets.preferred) : baseHeight;

    const sectionGap = spaces.spacingSm + spaces.spacingXs;

    const paddings: Record<ItemSize, Pick<ItemMeasures, "paddingHorizontal" | "paddingVertical">> = {
        small: {
            paddingVertical: spaces.spacingSm + spaces.spacingXxs,
            paddingHorizontal: sectionGap
        },
        xSmall: {
            paddingVertical: spaces.spacingXs + spaces.spacingXxs,
            paddingHorizontal: sectionGap
        },
        medium: {
            paddingHorizontal: spaces.spacingMd,
            paddingVertical: spaces.spacingMd
        }
    };

    return {
        ...paddings[size],
        gap: getRowIconGap({
            rowHeight: minHeight,
            controlHeights,
            spaces
        }),
        sectionGap,
        minHeight
    };
};

export const getItemTitleVariant = ({
    titleVariant,
    isSelected,
    size
}: ItemTitleVariantConstantType): keyof NCoreUIKit.Typography => {
    const currentVariant = titleVariant ?? ITEM_TITLE_VARIANTS[size];

    if(!isSelected) {
        return currentVariant;
    }

    return ROW_CARD_SELECTED_TITLE_VARIANTS[currentVariant] ?? currentVariant;
};

export const getItemGroupRadius = ({
    radiuses,
    borders
}: ItemGroupRadiusConstantType): number => {
    return concentric({
        outer: radiuses.surface,
        isCapsuleExempt: false,
        role: radiuses.surface,
        inset: borders.line
    });
};

export const getItemRadius = ({
    isGroupedOutline,
    borderRadius,
    radiuses,
    borders
}: ItemRadiusConstantType): number => {
    if(isGroupedOutline) {
        return getItemGroupRadius({
            radiuses,
            borders
        });
    }

    return borderRadius ?? radiuses.actions;
};

export const getItemColors = ({
    isDisabled,
    isSelected,
    variant,
    colors
}: ItemColorsConstantType): ItemColors => {
    const rowColors = getRowCardColors({
        isTransparentBackground: false,
        isDisabled,
        isSelected,
        colors
    });

    const selectedColor = getRowCardContainerColor({
        isTransparentBackground: false,
        isDisabled,
        isSelected,
        colors
    });

    return {
        containerColor: selectedColor ?? (variant === "filled" ? colors.system.state.overlay.fill.default : undefined),
        descriptionColor: rowColors.subTitleColor,
        iconColorKey: rowColors.iconColorKey,
        titleColor: rowColors.titleColor,
        iconColor: rowColors.iconColor
    };
};

export const getItemIconSize = ({
    iconSizes,
    size
}: ItemIconSizeConstantType): number => {
    return ITEM_SMALL_ICON_SIZES.includes(size) ? iconSizes.small : iconSizes.medium;
};

export const getItemAvatarSize = ({
    controlHeights,
    avatarSizes
}: ItemAvatarSizeConstantType): ItemAvatarSize => {
    const keys = Object.keys(avatarSizes) as Array<keyof NCoreUIKit.AvatarSizes>;

    const closest = keys.reduce((current, key) => {
        return Math.abs(avatarSizes[key] - controlHeights.medium) < Math.abs(avatarSizes[current] - controlHeights.medium) ? key : current;
    });

    return {
        size: avatarSizes[closest],
        key: closest
    };
};

export const getItemMediaWidth = ({
    controlHeights,
    avatarSizes,
    iconSizes,
    variant,
    size
}: ItemMediaWidthConstantType): number => {
    if(variant === "icon") {
        return controlHeights.small;
    }

    if(variant === "image") {
        return controlHeights.medium;
    }

    if(variant === "avatar") {
        return getItemAvatarSize({
            controlHeights,
            avatarSizes
        }).size;
    }

    return getItemIconSize({
        iconSizes,
        size
    });
};

export const getItemSeparatorInset = ({
    controlHeights,
    mediaVariant,
    touchTargets,
    avatarSizes,
    pointerType,
    iconSizes,
    spaces,
    size
}: ItemSeparatorInsetConstantType): number => {
    const measures = getItemMeasures({
        controlHeights,
        touchTargets,
        pointerType,
        spaces,
        size
    });

    if(!mediaVariant) {
        return measures.paddingHorizontal;
    }

    return measures.paddingHorizontal + getItemMediaWidth({
        variant: mediaVariant,
        controlHeights,
        avatarSizes,
        iconSizes,
        size
    }) + measures.gap;
};

const stylesheet = StyleSheet.create({
    layer: {
        ...StyleSheet.absoluteFill,
        ...webStyle({
            outlineStyle: "solid",
            outlineWidth: 0
        })
    },
    container: {
        justifyContent: "center",
        flexDirection: "column",
        alignSelf: "stretch",
        ...webStyle({
            outlineStyle: "solid",
            outlineWidth: 0
        })
    },
    passThrough: {
        pointerEvents: "box-none"
    },
    media: {
        justifyContent: "center",
        alignItems: "center",
        flexShrink: 0
    },
    groupContainer: {
        flexDirection: "column",
        alignSelf: "stretch"
    },
    content: {
        flexDirection: "column",
        minWidth: 0,
        flex: 1
    },
    mediaTop: {
        alignSelf: "flex-start"
    },
    row: {
        flexDirection: "row",
        alignItems: "center",
        alignSelf: "stretch"
    },
    inert: {
        pointerEvents: "none"
    },
    mediaTile: {
        borderStyle: "solid",
        overflow: "hidden"
    },
    actions: {
        flexDirection: "row",
        alignItems: "center",
        flexShrink: 0
    },
    strip: {
        alignSelf: "stretch"
    },
    separator: {
        alignSelf: "stretch"
    },
    clipped: {
        overflow: "hidden"
    },
    mediaImage: {
        height: "100%",
        width: "100%"
    }
});

export const useStyles = ({
    isReduceMotionEnabled,
    isGroupedOutline,
    currentMeasures,
    isFocusVisible,
    currentColors,
    isInteractive,
    borderRadius,
    shapeConfigs,
    durations,
    isLoading,
    borders,
    corners,
    variant,
    colors
}: ItemDynamicStyleType) => {
    const outlineInset = variant === "outline" ? borders.line : 0;

    const styles = {
        container: {
            paddingHorizontal: currentMeasures.paddingHorizontal - outlineInset,
            paddingVertical: currentMeasures.paddingVertical - outlineInset,
            minHeight: currentMeasures.minHeight,
            gap: currentMeasures.sectionGap
        } as Mutable<ViewStyle>,
        row: {
            gap: currentMeasures.gap
        } as Mutable<ViewStyle>
    };

    if(isGroupedOutline) {
        Object.assign(styles.container, getCornersStyle({
            corners: {
                borderStartStartRadius: corners.isFirst ? borderRadius : 0,
                borderStartEndRadius: corners.isFirst ? borderRadius : 0,
                borderEndStartRadius: corners.isLast ? borderRadius : 0,
                borderEndEndRadius: corners.isLast ? borderRadius : 0
            },
            shapeConfigs
        }), stylesheet.clipped);
    } else {
        Object.assign(styles.container, getCornerStyle({
            radius: borderRadius,
            shapeConfigs
        }));
    }

    if(variant === "outline") {
        styles.container.borderColor = colors.content.border.default;
        styles.container.borderWidth = borders.line;
        styles.container.borderStyle = "solid";
    }

    if(currentColors.containerColor) {
        styles.container.backgroundColor = currentColors.containerColor;
    }

    Object.assign(styles.container, webStyle(getMotionTransition({
        isReduceMotionEnabled,
        properties: [
            "background-color"
        ],
        intent: "hover",
        durations
    })));

    Object.assign(styles.container, getFocusRingStyle({
        isFocusVisible: isInteractive && isFocusVisible,
        isInset: isGroupedOutline,
        borders,
        colors
    }));

    if(isInteractive && isLoading) {
        Object.assign(styles.container, webStyle({
            cursor: "not-allowed"
        }));
    }

    return styles;
};

export default stylesheet;
